/**
 * Scope + button for per-drop group randomization
 * For Media Devoted | Created By Ciro Autiero
 */

import * as Popover from '@radix-ui/react-popover';
import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui';

export interface PerDropGroupSelectorRandomizeToolbarProps {
  randomizeScopeIds: string[];
  onClearRandomizeScopes: () => void;
  onFolderScopeChange: (folderId: string, checked: boolean) => void;
  folderScopeOptions: { id: string; name: string }[];
  showScopeSelect: boolean;
  randomizeButtonLabel: string;
  canRandomize: boolean;
  onRandomize: () => void;
}

export function PerDropGroupSelectorRandomizeToolbar({
  randomizeScopeIds,
  onClearRandomizeScopes,
  onFolderScopeChange,
  folderScopeOptions,
  showScopeSelect,
  randomizeButtonLabel,
  canRandomize,
  onRandomize,
}: PerDropGroupSelectorRandomizeToolbarProps) {
  if (!canRandomize) return null;
  const selectedScopes = new Set(randomizeScopeIds);
  const selectedNames = folderScopeOptions
    .filter((scope) => selectedScopes.has(scope.id))
    .map((scope) => scope.name);
  const summary =
    selectedNames.length === 0
      ? 'All groups'
      : selectedNames.length === 1
        ? selectedNames[0]
        : `${selectedNames.length} folders`;

  return (
    <div
      className="flex flex-wrap items-center justify-end gap-1"
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {showScopeSelect ? (
        <Popover.Root>
          <Popover.Trigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="per-drop-randomize-scope"
              className="h-6 max-w-[min(220px,42vw)] justify-between gap-1.5 px-2 text-[10px] font-normal"
              aria-label="Randomize scope: all groups or leaves under selected folders"
            >
              <span className="truncate">{summary}</span>
              <ChevronDown className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
            </Button>
          </Popover.Trigger>
          <Popover.Content
            align="end"
            sideOffset={4}
            className="z-[350] w-64 rounded-lg border bg-popover p-2 text-popover-foreground shadow-lg"
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="mb-1 px-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Randomize from
            </div>
            <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-muted">
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={randomizeScopeIds.length === 0}
                onChange={onClearRandomizeScopes}
                aria-label="Randomize all groups"
              />
              <span className="min-w-0 flex-1 truncate">All groups</span>
            </label>
            <div className="my-1 border-t border-border" />
            {folderScopeOptions.map((f) => (
              <label
                key={f.id}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-muted"
                title={f.name}
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={selectedScopes.has(f.id)}
                  onChange={(e) => onFolderScopeChange(f.id, e.target.checked)}
                  aria-label={`Randomize under ${f.name}`}
                />
                <span className="min-w-0 flex-1 truncate">{f.name}</span>
              </label>
            ))}
          </Popover.Content>
        </Popover.Root>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-5 px-1.5 text-[10px]"
        onClick={onRandomize}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {randomizeButtonLabel}
      </Button>
    </div>
  );
}
